'use client'
import { formatLongString } from '@morpho-org/morpho-ts'
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client'
import { Card } from '@morpho-org/ui-v6/server'
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui'
import { useState } from 'react'

import CollateralAtRiskChart from '@/components/charts/components/CollateralAtRiskChart'
import { useIsMobile } from '@/hooks/useBreakpoints'

import type { MarketCollateralAtRisk } from './collateralAtRisk.contract'

type Mode = 'collateralAssets' | 'collateralUsd'

const getModeOptions = (symbol: string): OptionSelect<Mode>[] => {
  return [
    {
      value: 'collateralUsd',
      label: 'USD'
    },
    {
      value: 'collateralAssets',
      label: formatLongString(symbol, 30)
    }
  ]
}

export interface ChartCollateralAtRiskClientProps {
  collateralAtRisk: MarketCollateralAtRisk
}

const ChartCollateralAtRiskClient = ({ collateralAtRisk }: ChartCollateralAtRiskClientProps) => {
  const isMobile = useIsMobile()
  const [mode, setMode] = useState<Mode>('collateralUsd')

  const { collateralToken, points } = collateralAtRisk
  const symbol = collateralToken?.symbol ?? ''
  const collateralAsset = collateralToken
    ? { symbol, decimals: collateralToken.decimals }
    : undefined
  const modeOptions = collateralToken ? getModeOptions(symbol) : undefined
  const unit = mode === 'collateralUsd' ? '$' : symbol
  const usdUnavailable =
    mode === 'collateralUsd' &&
    points.some(point => point.collateralUsd === null) &&
    !points.some(point => (point.collateralUsd ?? 0) > 0)

  return (
    <Card
      className="gap-sm py-lg px-md md:px-lg w-full flex-col max-md:gap-[16px]"
      withBorder
      data-testid="market-collateral-at-risk-chart"
    >
      <div className="gap-sm flex flex-wrap items-center justify-between">
        <div className="flex items-center gap-[2px]">
          <span className="text-secondary-foreground text-xs">Collateral at risk</span>
        </div>
        <div className="gap-sm flex flex-wrap items-center">
          {isMobile ? (
            <Select<Mode> options={modeOptions} value={mode} onChange={setMode} />
          ) : (
            <Tabs value={mode} onValueChange={value => setMode(value as Mode)}>
              <SegmentedControlList size="default" aria-label="Currency">
                {(modeOptions ?? []).map(option => (
                  <SegmentedControlItem key={option.value} value={option.value}>
                    {option.label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControlList>
            </Tabs>
          )}
        </div>
      </div>
      <div className="gap-2xs flex flex-col">
        <CollateralAtRiskChart
          statuses={{
            loading: false,
            error: usdUnavailable
          }}
          data={points}
          collateralAsset={collateralAsset}
          unit={unit}
          height={isMobile ? '172px' : '223px'}
        />
        <div className="mt-2xs flex justify-end">
          <span className="text-secondary-foreground text-xs">Oracle price change</span>
        </div>
      </div>
    </Card>
  )
}

export default ChartCollateralAtRiskClient
